import React, { useContext, useMemo } from "react";
import { Helmet } from "react-helmet";
import { Language } from "../../App";

/**
 * SEO & Structured Metadata component for the Mentorship page.
 * Manages dynamic document title, meta tags (OpenGraph, Twitter, App Links, Robots),
 * and Schema.org JSON-LD structured data for rich search engine indexing.
 */
const MentorshipSEO = ({ selectedCategoryName = null }) => {
  const { lang } = useContext(Language) || {};

  const isSpanish = useMemo(() => {
    try {
      if (typeof window !== "undefined") {
        const searchParams = new URLSearchParams(window.location.search);
        const urlLang = searchParams.get("lang");
        if (urlLang) {
          const lower = urlLang.toLowerCase().trim();
          if (lower === "es" || lower === "spanish" || lower === "sp") return true;
          if (lower === "en" || lower === "english") return false;
        }
        if (searchParams.has("es") || searchParams.has("spanish")) return true;
        if (window.location.pathname.toLowerCase().includes("/es")) return true;
      }
      const stored = localStorage.getItem("selectedLanguage");
      if (stored === "Spanish") return true;
      if (stored === "English") return false;
      return (
        lang?.code === "es" ||
        (typeof navigator !== "undefined" &&
          Boolean(navigator.language?.toLowerCase()?.startsWith("es")))
      );
    } catch {
      return false;
    }
  }, [lang]);

  // Compute canonical URL & origin safely
  const { siteOrigin, canonicalUrl, pageUrl } = useMemo(() => {
    const origin =
      typeof window !== "undefined" && window.location.origin
        ? window.location.origin
        : "https://ophthalmologyradar.com";

    const path = isSpanish ? "/mentorship/es" : "/mentorship";
    const cleanCanonical = `${origin}${path}`;

    return {
      siteOrigin: origin,
      canonicalUrl: cleanCanonical,
      pageUrl: cleanCanonical,
    };
  }, [isSpanish]);

  // Default social share image (1200x630 Mentorship Banner)
  const ogImage = useMemo(() => {
    const origin =
      typeof window !== "undefined" && window.location.origin
        ? window.location.origin
        : "https://ophthalmologyradar.com";

    return isSpanish ? `${origin}/mentorship-banner-es.png` : `${origin}/mentorship-banner.png`;
  }, [isSpanish]);

  // Dynamic Title
  const title = useMemo(() => {
    if (selectedCategoryName && selectedCategoryName !== "All") {
      return `Mentorship: ${selectedCategoryName} | Ophthalmology Radar`;
    }

    return "Mentorship | Ophthalmology Radar";
  }, [selectedCategoryName]);

  // Dynamic Description
  const description = useMemo(() => {
    if (selectedCategoryName && selectedCategoryName !== "All") {
      return isSpanish
        ? `Mentorship clínico 1 a 1 y discusión de casos de ${selectedCategoryName} en Ophthalmology Radar.`
        : `1-on-1 clinical mentorship and case discussions in ${selectedCategoryName} on Ophthalmology Radar.`;
    }

    return isSpanish
      ? "Conéctate con mentores oftalmológicos para orientación clínica 1 a 1 y discusión de casos en Ophthalmology Radar."
      : "Connect with ophthalmic mentors for 1-on-1 clinical guidance and case discussions on Ophthalmology Radar.";
  }, [selectedCategoryName, isSpanish]);

  // Dynamic Keywords
  const keywords = useMemo(() => {
    return isSpanish
      ? "mentorship oftalmología, mentores oftalmológicos, mentoría quirúrgica, orientación médica 1 a 1, casos clínicos complejos, mentor retina, mentor glaucoma, mentor córnea, mentor oculoplástica, Ophthalmology Radar"
      : "ophthalmology mentorship, ophthalmic mentors, surgical mentorship, eye doctor guidance, 1 on 1 clinical session, clinical case review, retina mentorship, glaucoma mentorship, oculoplastics mentor, cataract mentorship, Ophthalmology Radar";
  }, [isSpanish]);

  // Schema.org Structured Data
  const schemaJson = useMemo(() => {
    const schemaObj = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: title,
      description: description,
      url: pageUrl,
      inLanguage: isSpanish ? "es-ES" : "en-US",
      isPartOf: {
        "@type": "WebSite",
        name: "Ophthalmology Radar",
        url: siteOrigin,
      },
      about: [
        {
          "@type": "MedicalSpecialty",
          name: "Ophthalmology",
        },
        {
          "@type": "Thing",
          name: "Clinical Mentorship & Professional Medical Guidance",
        },
        {
          "@type": "Thing",
          name: "Ophthalmic Surgical Case Reviews",
        },
      ],
      mainEntity: {
        "@type": "ItemList",
        name: title,
        description: description,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            item: {
              "@type": "Service",
              serviceType: "1-on-1 Ophthalmic Mentorship",
              name: isSpanish
                ? "Tutorías 1 a 1 en Córnea y Superficie Ocular"
                : "1-on-1 Cornea & Ocular Surface Mentorship",
              description: isSpanish
                ? "Orientación especializada para ojo seco complejo, trasplante corneal y reconstrucción de la superficie ocular."
                : "Expert 1-on-1 clinical guidance for complex dry eye, corneal transplantation, and ocular surface reconstruction.",
              provider: {
                "@type": "Organization",
                name: "Ophthalmology Radar",
                url: siteOrigin,
              },
            },
          },
          {
            "@type": "ListItem",
            position: 2,
            item: {
              "@type": "Service",
              serviceType: "1-on-1 Ophthalmic Mentorship",
              name: isSpanish
                ? "Tutorías 1 a 1 en Retina Quirúrgica y Vítreo"
                : "1-on-1 Surgical Retina & Vitreous Mentorship",
              description: isSpanish
                ? "Discusión de casos de desprendimiento de retina, tracción vitreomacular y cirugía microincisional."
                : "In-depth case discussion on retinal detachment, vitreomacular traction, and micro-incisional surgery.",
              provider: {
                "@type": "Organization",
                name: "Ophthalmology Radar",
                url: siteOrigin,
              },
            },
          },
        ],
      },
    };

    return JSON.stringify(schemaObj);
  }, [title, description, pageUrl, siteOrigin, isSpanish]);

  return (
    <Helmet>
      {/* Primary HTML Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta
        name="robots"
        content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
      />
      <meta name="language" content={isSpanish ? "Spanish" : "English"} />

      {/* Multilingual Alternate Links (hreflang for international SEO) */}
      <link rel="alternate" hrefLang="en" href={`${siteOrigin}/mentorship`} />
      <link rel="alternate" hrefLang="es" href={`${siteOrigin}/mentorship/es`} />
      <link rel="alternate" hrefLang="x-default" href={`${siteOrigin}/mentorship`} />

      {/* Open Graph / Facebook / LinkedIn / WhatsApp */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Ophthalmology Radar" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content={
          isSpanish
            ? "Tutorías Oftalmológicas y Orientación Médica 1 a 1 | Ophthalmology Radar"
            : "Ophthalmic Mentorship & 1-on-1 Clinical Guidance | Ophthalmology Radar"
        }
      />
      <meta property="og:locale" content={isSpanish ? "es_ES" : "en_US"} />
      <meta property="og:locale:alternate" content={isSpanish ? "en_US" : "es_ES"} />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@MedicalRadar" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta
        name="twitter:image:alt"
        content={
          isSpanish
            ? "Tutorías Oftalmológicas y Orientación Médica 1 a 1 | Ophthalmology Radar"
            : "Ophthalmic Mentorship & 1-on-1 Clinical Guidance | Ophthalmology Radar"
        }
      />

      {/* Deep Linking App Metadata */}
      <meta property="al:ios:app_name" content="Medicalradar" />
      <meta property="al:ios:app_store_id" content="1605786600" />
      <meta property="al:ios:url" content={pageUrl} />
      <meta property="al:android:app_name" content="MedicalRadar" />
      <meta
        property="al:android:package"
        content="com.drudotstech.medicalradar"
      />
      <meta property="al:android:url" content={pageUrl} />

      {/* Schema.org Structured Data */}
      <script type="application/ld+json">{schemaJson}</script>
    </Helmet>
  );
};

export default React.memo(MentorshipSEO);
